import { useState } from 'react';
import { api } from '../api';

/**
 * Opens Plaid Link to connect a new credit card or bank account.
 *
 * Flow:
 *   1. POST /api/plaid/link_token  → get a one-time link_token
 *   2. Plaid.create({ token }).open()  → Plaid shows its secure overlay
 *   3. onSuccess(public_token) fires when the user finishes authorizing
 *   4. We prompt for a friendly name
 *   5. POST /api/plaid/exchange { public_token, display_name }
 *   6. Done — the account is persisted in D1, nightly sync will pick it up
 */
export function ConnectCardButton({ onConnected }: { onConnected: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start() {
    setError(null);
    setBusy(true);

    if (!window.Plaid) {
      setError('Plaid Link SDK not loaded. Refresh the page and try again.');
      setBusy(false);
      return;
    }

    // 1. Get a link_token from the Worker
    let link_token: string;
    try {
      const res = await api.plaidLinkToken();
      link_token = res.link_token;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`Couldn't start Plaid Link: ${msg}`);
      setBusy(false);
      return;
    }

    // 2 & 3. Open Plaid Link
    const handler = window.Plaid.create({
      token: link_token,
      onSuccess: async (public_token, metadata) => {
        const fallbackName = metadata.institution?.name
          ? `${metadata.institution.name}${metadata.accounts[0]?.mask ? ' ••' + metadata.accounts[0].mask : ''}`
          : 'New account';
        // 4. Prompt for a friendly name
        const display_name = window.prompt(
          'Name this account (shows up on the dashboard):',
          fallbackName
        );
        if (!display_name) {
          setBusy(false);
          return;
        }
        try {
          // 5. Exchange on the Worker
          await api.plaidExchange(public_token, display_name);
          onConnected();
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
        } finally {
          setBusy(false);
        }
      },
      onExit: (err) => {
        if (err) setError(err.display_message ?? err.error_message ?? 'Plaid Link exited');
        setBusy(false);
      },
    });
    handler.open();
  }

  return (
    <>
      <button
        onClick={start}
        disabled={busy}
        className="text-sm px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
      >
        {busy ? 'Connecting…' : 'Connect a card'}
      </button>
      {error && (
        <div className="fixed bottom-4 right-4 max-w-sm bg-rose-50 border border-rose-200 text-rose-800 text-sm p-3 rounded-lg shadow-lg z-50">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-rose-600 hover:text-rose-900">✕</button>
        </div>
      )}
    </>
  );
}
